import React, { useRef, useState } from 'react';
import { Check } from 'lucide-react';
import type { CRMUser } from '../../types';
import { useAuth } from '../../utils/AuthContext';
import { mentionsNotifySomeone } from '../../utils/untaggedCommentConfirm';
import RichCommentEditor, { type RichCommentEditorHandle, type RichCommentEditorPayload } from './RichCommentEditor';
import UntaggedSendHint, { useUntaggedSendGate } from './UntaggedSendHint';

export type QuestionAnswerSlotProps = {
  questionId?: string;
  hasReplies: boolean;
  mentionableUsers: CRMUser[];
  supplierOrgNameById?: Map<string, string>;
  taskId: string;
  disabled: boolean;
  enableTeamMention?: boolean;
  mentionPrioritizePartners?: boolean;
  assignedArchitectUids?: string[];
  onSubmit: (payload: RichCommentEditorPayload) => Promise<void>;
  /** When true, an answer with no @mention asks for a second click before it posts. */
  confirmWhenUntagged?: boolean;
};

/** Inline answer editor under a Question comment — any thread participant can use it. */
const QuestionAnswerSlot: React.FC<QuestionAnswerSlotProps> = ({
  questionId,
  hasReplies,
  mentionableUsers,
  supplierOrgNameById,
  taskId,
  disabled,
  enableTeamMention = false,
  mentionPrioritizePartners = false,
  assignedArchitectUids,
  onSubmit,
  confirmWhenUntagged = false,
}) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(!hasReplies);
  const [draftHtml, setDraftHtml] = useState('');
  const answerRef = useRef<RichCommentEditorHandle>(null);
  const [busy, setBusy] = useState(false);
  const untaggedSend = useUntaggedSendGate(draftHtml);

  const send = async (payload?: RichCommentEditorPayload, source: 'click' | 'keyboard' = 'click') => {
    const next = payload ?? answerRef.current?.getPayload();
    if (!next || next.isEmpty) return;
    if (confirmWhenUntagged && !untaggedSend.allow(mentionsNotifySomeone(next.mentions, user?.id), source)) return;
    setBusy(true);
    try {
      await onSubmit(next);
      answerRef.current?.clear();
      setDraftHtml('');
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        className="tc-add-answer"
        onClick={() => setOpen(true)}
        disabled={disabled}
      >
        Add another answer
      </button>
    );
  }

  return (
    <div className="tc-answer-box" data-question-id={questionId}>
      <RichCommentEditor
        ref={answerRef}
        mentionableUsers={mentionableUsers}
        supplierOrgNameById={supplierOrgNameById}
        taskId={taskId}
        onChange={setDraftHtml}
        onSubmit={p => void send(p, 'keyboard')}
        placeholder="Write your answer…"
        disabled={disabled || busy}
        enableTeamMention={enableTeamMention}
        mentionPrioritizePartners={mentionPrioritizePartners}
        assignedArchitectUids={assignedArchitectUids}
      />
      <div className="tc-answer-box-actions">
        {hasReplies ? (
          <button
            type="button"
            className="tc-edit-cancel"
            onClick={() => setOpen(false)}
            disabled={busy}
          >
            Cancel
          </button>
        ) : null}
        <UntaggedSendHint open={confirmWhenUntagged && untaggedSend.open} onDismiss={untaggedSend.dismiss}>
        <button
          type="button"
          className="tc-edit-save"
          onClick={() => void send()}
          disabled={disabled || busy}
        >
          <Check size={12} /> {busy ? 'Sending…' : 'Answer'}
        </button>
        </UntaggedSendHint>
      </div>
    </div>
  );
};

export default QuestionAnswerSlot;
